import React, { Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { AuthProvider } from "./contexts/AuthContext";

// 🎯 COMPOSANTS PRINCIPAUX (chargés immédiatement)
import Header from "./components/Header";
import BreadcrumbsNavigation from "./components/BreadcrumbsNavigation";
import AccueilComplet from "./components/AccueilComplet";

// ⚡ LAZY LOADING - Composants chargés à la demande
const LandingOptimized = lazy(() => import("./components/LandingOptimized"));
const InterfaceIA = lazy(() => import("./components/InterfaceIA"));
const Success = lazy(() => import("./pages/Success"));
const Support = lazy(() => import("./components/Support"));
const Demo = lazy(() => import("./components/Demo"));
const Stocks = lazy(() => import("./pages/Stocks"));

// 📝 PAGES CONVERSION (lazy loaded)
const InscriptionElectriciensPage = lazy(() => import("./pages/InscriptionElectriciensPage"));
const InscriptionPlombiersPage = lazy(() => import("./pages/InscriptionPlombiersPage"));
const InscriptionMaconsPage = lazy(() => import("./pages/InscriptionMaconsPage"));
const InscriptionCoiffeursPage = lazy(() => import("./pages/InscriptionCoiffeursPage"));
const InscriptionGaragistesPage = lazy(() => import("./pages/InscriptionGaragistesPage"));
const InscriptionJardiniersPage = lazy(() => import("./pages/InscriptionJardiniersPage"));
const InscriptionPeintresPage = lazy(() => import("./pages/InscriptionPeintresPage"));
const InscriptionMenuisiersPage = lazy(() => import("./pages/InscriptionMenuisiersPage"));
const InscriptionFleuristesPage = lazy(() => import("./pages/InscriptionFleuristesPage"));
const InscriptionCarreleursPage = lazy(() => import("./pages/InscriptionCarreleursPage"));
const InscriptionEstheticiensPage = lazy(() => import("./pages/InscriptionEstheticiensPage"));
const InscriptionMasseursPage = lazy(() => import("./pages/InscriptionMasseursPage"));
const InscriptionMenagePage = lazy(() => import("./pages/InscriptionMenagePage"));
const InscriptionCarrossiersPage = lazy(() => import("./pages/InscriptionCarrossiersPage"));
const InscriptionControleTechniquePage = lazy(() => import("./pages/InscriptionControleTechniquePage"));
const InscriptionRamoneursPage = lazy(() => import("./pages/InscriptionRamoneursPage"));
const InscriptionCouvreursPage = lazy(() => import("./pages/InscriptionCouvreursPage"));
const ContactDemoPage = lazy(() => import("./pages/ContactDemoPage"));

// 🎯 PAGES B2B - Routes dynamiques centralisées via /m/:metierId/b2b

// 🏗️ PAGES MÉTIERS ARTISANALES (lazy - obsolètes, remplacées par routes dynamiques)
const PlombiersPage = lazy(() => import("./pages/PlombiersPage"));
const ElectriciensPage = lazy(() => import("./pages/ElectriciensPage"));
const CoiffeursPage = lazy(() => import("./pages/CoiffeursPage"));
const JardiniersPage = lazy(() => import("./pages/JardiniersPage"));
const FleuristesPage = lazy(() => import("./pages/FleuristesPage"));
const PeintresPage = lazy(() => import("./pages/PeintresPage"));
const MaconsPage = lazy(() => import("./pages/MaconsPage"));
const MenuisiersPage = lazy(() => import("./pages/MenuisiersPage"));
const CarreleursPage = lazy(() => import("./pages/CarreleursPage"));
const EstheticiensPage = lazy(() => import("./pages/EstheticiensPage"));
const MasseusesPage = lazy(() => import("./pages/MasseusesPage"));
const GaragistesPage = lazy(() => import("./pages/GaragistesPage"));
const MenagePage = lazy(() => import("./pages/MenagePage"));
const ControleTechniquePage = lazy(() => import("./pages/ControleTechniquePage"));
const RamoneursPage = lazy(() => import("./pages/RamoneursPage"));
const CouvreursPage = lazy(() => import("./pages/CouvreursPage"));

// 💰 COMPOSANTS COMMERCIAUX LYS (lazy loaded)
const PricingLYS = lazy(() => import("./components/PricingLYS"));
const RevenueDashboard = lazy(() => import("./components/RevenueDashboard"));
const OnboardingFlow = lazy(() => import("./components/OnboardingFlow"));
const ClientDashboard = lazy(() => import("./components/ClientDashboard"));
const ClientDashboardAdaptatif = lazy(() => import("./components/ClientDashboardAdaptatif"));
const WhatYouGet = lazy(() => import("./components/WhatYouGet"));

// 📊 COMPOSANTS ANALYTICS & TRACKING (lazy loaded)
const AnalyticsDashboard = lazy(() => import("./components/analytics/AnalyticsDashboard"));
const ConversionReports = lazy(() => import("./components/analytics/ConversionReports"));
const AnalyticsHub = lazy(() => import("./components/AnalyticsHub"));
const NavigationTestPage = lazy(() => import("./pages/NavigationTestPage"));

// 🔐 PAGES AUTHENTIFICATION (lazy loaded)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));

// 📋 PAGES FONCTIONNELLES DASHBOARD (lazy loaded)
const Devis = lazy(() => import("./pages/Devis"));
const Clients = lazy(() => import("./pages/Clients"));
const Planning = lazy(() => import("./pages/Planning"));
const Factures = lazy(() => import("./pages/Factures"));
const Sites = lazy(() => import("./pages/Sites"));
const Profil = lazy(() => import("./pages/Profil"));
const Services = lazy(() => import("./pages/Services"));
const SMS = lazy(() => import("./pages/SMS"));
const Analytics = lazy(() => import("./pages/Analytics"));
const TeamManagement = lazy(() => import("./pages/TeamManagement"));
const BrandingSettings = lazy(() => import("./pages/BrandingSettings"));
const AdvancedReports = lazy(() => import("./pages/AdvancedReports"));
const APIDocumentation = lazy(() => import("./pages/APIDocumentation"));
const RelancesAutomatiques = lazy(() => import("./components/RelancesAutomatiques"));
const BasePrixMetier = lazy(() => import("./components/BasePrixMetier"));
const SiteVitrineBuilder = lazy(() => import("./components/SiteVitrineBuilder"));
const MesSitesVitrines = lazy(() => import("./pages/MesSitesVitrines"));

// Composants chargés immédiatement (non lazy)
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import Footer from "./components/Footer";
import SecurityNotification from "./components/SecurityNotification";
// 🚀 Nouvelles routes dynamiques unifiées (Showcase/Inscription/B2B)
import { MetierShowcaseParam, MetierInscriptionParam, MetierB2BParam } from "./routes/metierRoutes";

// 🎨 Composant Loader pour Suspense
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress size={60} />
  </Box>
);

function App() {
  const location = useLocation();
  
  // Ne pas afficher les breadcrumbs sur la page d'accueil
  const showBreadcrumbs = location.pathname !== '/';

  return (
    <AuthProvider>
      <Header />
      {showBreadcrumbs && <BreadcrumbsNavigation />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<AccueilComplet />} />
          <Route path="/support" element={<Support />} />
          <Route path="/demo" element={<Demo />} />
          <Route path="/ia" element={<InterfaceIA />} />
          <Route path="/landing" element={<LandingOptimized />} />
          <Route path="/success" element={<Success />} />


        {/* 📝 ROUTES INSCRIPTION - 17 MÉTIERS */}
        <Route path="/inscription-electriciens" element={<InscriptionElectriciensPage />} />
        <Route path="/inscription-plombiers" element={<InscriptionPlombiersPage />} />
        <Route path="/inscription-macons" element={<InscriptionMaconsPage />} />
        <Route path="/inscription-coiffeurs" element={<InscriptionCoiffeursPage />} />
        <Route path="/inscription-garagistes" element={<InscriptionGaragistesPage />} />
        <Route path="/inscription-jardiniers" element={<InscriptionJardiniersPage />} />
        <Route path="/inscription-peintres" element={<InscriptionPeintresPage />} />
        <Route path="/inscription-menuisiers" element={<InscriptionMenuisiersPage />} />
        <Route path="/inscription-fleuristes" element={<InscriptionFleuristesPage />} />
        <Route path="/inscription-carreleurs" element={<InscriptionCarreleursPage />} />
        <Route path="/inscription-estheticiennes" element={<InscriptionEstheticiensPage />} />
        <Route path="/inscription-masseurs" element={<InscriptionMasseursPage />} />
        <Route path="/inscription-menage" element={<InscriptionMenagePage />} />
        <Route path="/inscription-carrossiers" element={<InscriptionCarrossiersPage />} />
        <Route path="/inscription-controle-technique" element={<InscriptionControleTechniquePage />} />
        <Route path="/inscription-ramoneurs" element={<InscriptionRamoneursPage />} />
        <Route path="/inscription-couvreurs" element={<InscriptionCouvreursPage />} />
        <Route path="/contact-demo" element={<ContactDemoPage />} />

        {/* 🎯 ROUTES B2B - Utilise les routes dynamiques /m/:metierId/b2b ci-dessous */}

        <Route path="/tarifs" element={<PricingLYS />} />
        <Route path="/revenus" element={<RevenueDashboard />} />
        <Route path="/inscription" element={<OnboardingFlow />} />
        {/* 🔐 ROUTES AUTHENTIFICATION & DASHBOARDS */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/electricien" element={<Dashboard />} />
        <Route path="/dashboard/plombier" element={<Dashboard />} />
        <Route path="/dashboard/macon" element={<Dashboard />} />
        <Route path="/dashboard/coiffeur" element={<Dashboard />} />
        <Route path="/dashboard/garagiste" element={<Dashboard />} />
        <Route path="/dashboard/jardinier" element={<Dashboard />} />
        <Route path="/dashboard/peintre" element={<Dashboard />} />
        <Route path="/dashboard/menuisier" element={<Dashboard />} />
        <Route path="/dashboard/fleuriste" element={<Dashboard />} />
        <Route path="/dashboard/carreleur" element={<Dashboard />} />
        <Route path="/dashboard/estheticien" element={<Dashboard />} />
        <Route path="/dashboard/masseur" element={<Dashboard />} />
        <Route path="/dashboard/menage" element={<Dashboard />} />
        {/* <Route path="/dashboard/carrossier" element={<Dashboard />} /> */}
        {/* <Route path="/dashboard/controle-technique" element={<Dashboard />} /> */}
        <Route path="/dashboard-admin" element={<ClientDashboardAdaptatif />} />
        <Route path="/dashboard-old" element={<ClientDashboard />} />
        
        {/* 📋 ROUTES PAGES FONCTIONNELLES */}
        <Route path="/devis" element={<Devis />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/planning" element={<Planning />} />
        <Route path="/factures" element={<Factures />} />
        <Route path="/sites" element={<Sites />} />
        <Route path="/profil" element={<Profil />} />
        <Route path="/services" element={<Services />} />
        <Route path="/sms" element={<SMS />} />
        <Route path="/stocks" element={<Stocks />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/team" element={<TeamManagement />} />
        <Route path="/branding" element={<BrandingSettings />} />
        <Route path="/reports" element={<AdvancedReports />} />
        <Route path="/relances" element={<RelancesAutomatiques />} />
        <Route path="/base-prix" element={<BasePrixMetier />} />
        <Route path="/creer-site-vitrine" element={<SiteVitrineBuilder />} />
        <Route path="/mes-sites-vitrines" element={<MesSitesVitrines />} />
        
        <Route path="/admin-login" element={<AdminLoginPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/ce-que-vous-obtenez" element={<WhatYouGet />} />

        {/* 📊 ROUTES ANALYTICS & TRACKING */}
        <Route path="/analytics" element={<AnalyticsHub />} />
        <Route path="/analytics-hub" element={<AnalyticsHub />} />
        <Route path="/conversion-reports" element={<AdminProtectedRoute><ConversionReports /></AdminProtectedRoute>} />
        {/* <Route path="/ab-testing" element={<AdminProtectedRoute><ABTestingSystem /></AdminProtectedRoute>} /> */}
        <Route path="/analytics-dashboard" element={<AdminProtectedRoute><AnalyticsDashboard /></AdminProtectedRoute>} />
        <Route path="/navigation-test" element={<NavigationTestPage />} />

        {/* 🚀 Routes dynamiques unifiées pour tous les métiers (nouveau système) */}
        {/* Exemple: /m/electriciens, /m/electriciens/inscription, /m/electriciens/b2b */}
        <Route path="/m/:metierId" element={<MetierShowcaseParam />} />
        <Route path="/m/:metierId/inscription" element={<MetierInscriptionParam />} />
        <Route path="/m/:metierId/b2b" element={<MetierB2BParam />} />
      </Routes>
      </Suspense>
      <Footer />
      <SecurityNotification />
    </AuthProvider>
  );
}

export default App;
